<template>
  <AppSidebar
    :id="sidebarId"
    has-pull-btn
    :visible="showFilters"
    class="job-sites-sidebar"
    :dismiss-on-body-click="false"
    @show="$emit('show')"
    @close="$emit('close')"
  >
    <template v-if="hasJobSites">
      <DynamicScroller
        :items="filteredJobSites"
        :min-item-size="90"
        class="scroller"
      >
        <template #before>
          <button
            type="button"
            class="jobsites-sidebar-close btn btn-icon close ml-auto"
            @click="$emit('close')"
          >
            <i class="hi hi-close hi-9" />
          </button>
          <h4>Job sites</h4>
          <template v-if="filteredJobSites.length || hasJobSites">
            <div class="btn-add-job-site-wrapper add-job-site-buttons">
              <AppButton
                type="primary"
                @click="$emit('open-wizard')"
              >
                Add {{ jobSiteLabel }}
              </AppButton>
              <AppButton
                type="outline"
                @click="$emit('open-import')"
              >
                Import {{ 2 | pluralize(jobSiteLabel) }}
              </AppButton>
            </div>
            <AppInputSearch>
              <AppInput
                type="search"
                :debounce="150"
                :placeholder="inputSearchJobSitesPlaceholder"
                @input="$emit('search', $event)"
              />
            </AppInputSearch>
            <div class="mb-10">
              <JobSitesSortBy
                :options="jobSitesGroupByOptions()"
                label="Sort by"
              />
              <div
                v-if="selectedMemberIdsIsEmpty"
                class="map-sidebar-list-utility map-sidebar-filter-toggle pull-right p-0"
              >
                <a @click="toggleMemberFilter">
                  <span v-if="showMemberFilter"> Hide filters </span>
                  <span v-else>
                    <i class="hi hi-bars hi-16 mr-5" />
                    <span> View filters </span>
                  </span>
                </a>
              </div>
            </div>
            <JobSitesMemberFilter
              v-show="showMemberFilter"
              :selected-member-ids="selectedMemberIds"
              :options="membersForFilter"
              @input="$emit('filter', $event)"
            />
          </template>

          <template v-if="!filteredJobSites.length">
            <div class="text-grey mt-20">No {{ 2 | pluralize(jobSiteLabel) }} found</div>
          </template>
        </template>
        <template #default="{ item, index, active }">
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :data-index="index"
          >
            <AppSelectListItem
              :id="selectListItemId(item.id)"
              :class="{ active: isSelected(item.id) }"
              :title="item.name"
              @click="onSelectListClick(item)"
            >
              <template #label>
                <div class="job-site-item">
                  <div
                    class="custom-select-list-item-avatar"
                    :style="{ backgroundColor: item.color }"
                  >
                    <img
                      :src="whiteMarkerIcon"
                      :alt="item.name"
                    />
                  </div>
                  <div class="job-site-item-content">
                    <div class="text-overflow">
                      {{ item.name }}
                    </div>
                    <span class="hint is-regular">{{ item.address }}</span>
                  </div>
                </div>
              </template>

              <template #actions>
                <div class="action-buttons">
                  <JobSiteAction
                    :item="item"
                    icon="edit"
                    :title="`Edit ${item.name}`"
                    append-body
                    @click="openEditDialog(item)"
                  />
                  <JobSiteAction
                    :item="item"
                    icon="delete"
                    :title="`Remove ${item.name}`"
                    append-body
                    @click="archiveJobSite(item)"
                  />
                </div>
              </template>
            </AppSelectListItem>
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>
    </template>

    <div
      v-else
      class="p-30"
    >
      <div class="job-sites-sidebar-empty-state text-lighter text-center">
        <img
          :src="noSitesImageUrl"
          alt="no job sites for this day"
          class="mt-50"
        />
        <div class="mb-20 mt-10">No job sites created yet</div>
        <div class="add-job-site-buttons">
          <AppButton
            type="primary"
            modifiers="mt-20"
            @click="$emit('open-wizard')"
          >
            Add {{ jobSiteLabel }}
          </AppButton>
          <AppButton
            type="outline"
            modifiers="mt-20"
            @click="$emit('open-import')"
          >
            Import {{ 2 | pluralize(jobSiteLabel) }}
          </AppButton>
        </div>
      </div>
    </div>
  </AppSidebar>
</template>

<script>
import { mapGetters } from 'vuex';
import { ArchiveJobSite } from '~/mixins';
import { IMAGE_NO_SITES, ICON_MARKER_WHITE } from '~/rails_assets';
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import select2DropdownParentMixin from '~/mixins/Select2DropdownParent';

export default {
  name: 'JobSitesSidebar',
  components: {
    DynamicScroller,
    DynamicScrollerItem,
  },
  mixins: [ArchiveJobSite, select2DropdownParentMixin],
  props: {
    editing: Boolean,
    filteredJobSites: Array,
    showFilters: {
      type: Boolean,
      default: true,
    },
    selectedMemberIds: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectedJobSites: [],
      showMemberFilter: false,
      jobSiteLabel: 'job site',
      noSitesImageUrl: IMAGE_NO_SITES,
      whiteMarkerIcon: ICON_MARKER_WHITE,
      inputSearchJobSitesPlaceholder: 'Search job sites',
    };
  },
  computed: {
    ...mapGetters(['selectedJobSiteId', 'hasJobSites', 'scrolledJobSiteId', 'membersForFilter']),
    sidebarId() {
      return 'job-sites-sidebar';
    },
    selectedMemberIdsIsEmpty() {
      return this.selectedMemberIds.length === 0;
    },
  },
  watch: {
    scrolledJobSiteId(value) {
      if (value) {
        const sidebar = document.getElementById(this.sidebarId);
        const listItem = document.getElementById(this.selectListItemId(value));

        if (sidebar && listItem) {
          sidebar.scrollTop = listItem.offsetTop;
          this.$store.dispatch('clearScrollToJobSite');
        }
      }
    },
  },
  methods: {
    onSelectListClick(jobSite) {
      if (this.editing) {
        return;
      }
      if (jobSite) {
        this.selectedJobSites = [jobSite.id];
        this.$emit('item-click', jobSite);
      }
    },
    openEditDialog(jobSite) {
      this.$emit('edit', jobSite.id);
    },
    toggleMemberFilter() {
      this.showMemberFilter = !this.showMemberFilter;
    },
    jobSitesGroupByOptions() {
      return [
        { id: 'last_visited', text: 'Last visited' },
        { id: 'alpha', text: 'Alphabetically' },
      ];
    },
    selectListItemId(id) {
      return `select-list-item-${id}`;
    },
    isSelected(currentId) {
      return this.selectedJobSiteId === currentId;
    },
  },
};
</script>
